import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';

export const ManuscriptReaderPage = () => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [bookmarks, setBookmarks] = useState<number[]>([1, 5]);
  const [notes, setNotes] = useState<Array<{ chapter: number; position: number; text: string; date: string }>>([
    {
      chapter: 0,
      position: 350,
      text: 'Great opening! Really sets the mood.',
      date: '2024-02-01',
    },
    {
      chapter: 1,
      position: 150,
      text: 'Emma\'s character feels very relatable here.',
      date: '2024-02-02',
    },
  ]);

  // Extract manuscript ID from URL - in real app would use router
  const manuscriptId = window.location.pathname.match(/\/reader\/manuscripts\/(\d+)\/read/)?.[1];

  // Mock data - in real app, would fetch from API
  const manuscript = {
    id: manuscriptId,
    title: 'Echoes of the Archive',
    author: 'Eleanor Hart',
    genre: 'Historical Fiction',
    wordCount: 95000,
    totalChapters: 24,
    chapters: [
      {
        number: 0,
        title: 'Prologue',
        wordCount: 2500,
        content: `The archive was dying.

Dr. Sarah Mitchell stood in the main reading room, watching dust motes dance in the afternoon light filtering through tall windows. Once, this had been the heart of academic research in the city—a temple of knowledge where scholars from around the world came to study rare manuscripts and forgotten histories.

Now, it was scheduled for demolition.

"Dr. Mitchell?" A young voice pulled her from her reverie.

She turned to find James, one of the few remaining archivists, hovering near the circulation desk with an armful of boxes. At twenty-three, he was young enough to find another job easily, unlike Sarah, who had devoted the last thirty years of her life to this place.

"The last of the personal items from the third floor," he said, setting the boxes down with a thud. "Most of it's just junk, but I thought you'd want to look through it before we toss everything."

Sarah nodded, kneeling beside the boxes. Her knees protested—a reminder that she wasn't as young as she used to be. Inside, she found the usual detritus: outdated catalogs, broken bookends, faded photographs of long-retired staff members.

Then her fingers brushed against something different. A leather-bound journal, small enough to fit in her palm, wedged between two moldy card catalogs.

The leather was aged but well-preserved, the kind of quality that suggested it had been expensive when new. No title graced the cover, no author's name. Just a small symbol embossed in gold: a key crossed with a quill.

"What's that?" James asked, peering over her shoulder.

"I'm not sure." Sarah carefully opened the journal. The pages were filled with dense, handwritten text in black ink. The handwriting was elegant, practiced—the work of someone educated in an era when penmanship mattered.

The first entry was dated March 15, 1943.

"World War II," James observed. "Might be worth something to a collector."

But Sarah wasn't listening. Her eyes had caught on the first line:

"They think I don't know what they've hidden in the sub-basement. They think I'm just another clerk, too insignificant to notice. But I've found it—the Archive's greatest secret, the one they would kill to protect."

A chill ran down Sarah's spine despite the stuffy warmth of the reading room.

"Dr. Mitchell?" James's voice seemed to come from very far away. "You okay? You look like you've seen a ghost."

Sarah closed the journal carefully, her mind racing. The archive had a sub-basement? In thirty years, she'd never heard of such a thing. The building plans showed only two levels below ground—storage and mechanical systems.

"James," she said slowly, "have you ever been to the sub-basement?"

He frowned. "We don't have a sub-basement. Just the regular basement and the lower storage level."

"That's what I thought."

But as Sarah looked down at the journal in her hands, she knew with absolute certainty that whoever had written these words had known something that had been deliberately forgotten. And now, with only three weeks before the wrecking balls arrived, she might be the last person who would ever have the chance to find out what it was.

The question was: did she dare look?

Outside, the sun began to set, casting long shadows through the reading room. In the fading light, Sarah could have sworn she saw something move in the corner where the oldest card catalog stood—the one that predated the computer system, the one that still listed acquisitions from the archive's founding in 1889.

She blinked, and whatever it was—if it had ever been there at all—was gone.

But the journal in her hands felt heavier than it should, as if it carried the weight of all the secrets buried in this dying building.

Sarah Mitchell had always considered herself a rational woman, a scholar who dealt in facts and primary sources. But as she slipped the journal into her bag and said goodnight to James, she couldn't shake the feeling that she was being watched.

The archive had three weeks left to live.

Sarah had a feeling that might be just enough time to uncover what it had died to protect.`,
      },
      {
        number: 1,
        title: 'Chapter One',
        wordCount: 4200,
        content: `Sarah's apartment was on the third floor of a converted warehouse in the arts district—one of the few neighborhoods in the city that still had character, though the coffee shops and artisanal bakeries were slowly being replaced by chain stores and luxury condos.

She lived alone. Had lived alone for the past fifteen years, ever since her divorce from Michael. The split had been amicable, as far as these things went. He'd wanted children; she'd wanted her work. In the end, the archive had won.

Now she sat at her kitchen table, the mysterious journal open before her, a cup of tea cooling at her elbow.

March 15, 1943

They think I don't know what they've hidden in the sub-basement. They think I'm just another clerk, too insignificant to notice. But I've found it—the Archive's greatest secret, the one they would kill to protect.

I should burn this journal. I should forget what I've seen. But I'm a librarian at heart, and we document everything, even our own doom.

My name is Margaret Ashford. I'm twenty-four years old. If you're reading this, I'm probably dead.

Sarah felt that chill again. She turned the page.

I came to work at the City Archive in the fall of 1941, fresh out of library school. The war was already raging in Europe, though America wouldn't join for another few months. I was grateful to have found work—most of the men had enlisted or been drafted, and suddenly women were needed to fill positions that had been closed to us before.

The Archive was imposing even then. Five stories of granite and marble, filled with the collected memory of the city: birth records, death certificates, property deeds, maps, photographs, and thousands upon thousands of documents dating back to the city's founding.

I was assigned to the cataloging department, working under Mrs. Eleanor Graves, a formidable woman in her sixties who ran her domain with military precision. She taught me the filing systems, the catalog protocols, the careful handling of fragile documents.

She also taught me to mind my own business.

"Curiosity," she told me on my first day, "is a quality we prize in scholars. In archivists, it can be dangerous."

I didn't understand what she meant. Not then.

Sarah looked up from the journal, her mind spinning. Margaret Ashford. The name wasn't familiar, but that wasn't surprising—the archive had employed hundreds of people over its lifetime, and staff from the 1940s would be long gone by now.

But this mention of a sub-basement...

Sarah reached for her laptop and pulled up the architectural drawings for the archive building. She'd studied them countless times during renovation discussions over the years. The structure was relatively straightforward: five floors above ground, two below.

No sub-basement.

She returned to the journal.

March 20, 1943

I wasn't supposed to be in the building after hours. But Mrs. Graves had assigned me a large batch of new acquisitions to catalog, and I'd fallen behind. Rather than face her disappointment, I stayed late, working by lamplight in the cataloging room.

It was past midnight when I heard the voices.

Male voices, echoing from somewhere below. I wouldn't have thought anything of it—the janitor sometimes worked nights—but these voices were arguing, urgent and angry.

Then I heard my name.

"What about the Ashford girl?" one voice said. "She was working late. What if she saw something?"

"She's just a clerk," another voice replied. "Even if she saw us, she wouldn't know what she was looking at."

I froze at my desk, my hand still holding a catalog card. They were talking about me.

"We can't take that risk," the first voice insisted. "Not with what we're protecting."

There was a long pause. Then: "I'll talk to Mrs. Graves. She'll reassure us about the girl's... discretion."

Footsteps on the stairs. Moving up, toward the cataloging room.

I don't remember deciding to hide. My body simply moved, ducking under the desk, pulling my chair in to conceal myself. My heart hammered so hard I was certain they would hear it.

The door opened. Two sets of footsteps entered the room.

"See? No one here. She must have left earlier." This was a voice I recognized: Mr. Harrison, the Archive Director.

"Her coat is still on the rack," the other man observed. I didn't know this voice, but it carried authority, the kind that comes from being used to command.

A long silence. I pressed myself against the back of the desk, barely breathing.

"She's probably in the ladies' room," Mr. Harrison said finally. "Come on. We need to finish before the morning shift arrives."

They left. I waited under that desk for what felt like hours, too terrified to move.

When I finally crept out, dawn was breaking. I grabbed my coat and fled.

I should have kept running. I should have found another job, another city, another life.

But I was young and foolish, and I wanted to know what they were protecting.

Sarah realized her tea had gone completely cold. She checked her watch: 2 AM. She'd been reading for hours.

She should go to bed. She had to be at the archive early tomorrow—today—to continue the heartbreaking work of packing up a lifetime of research.

Instead, she turned the page.

The journal continued for another hundred pages, Margaret's elegant handwriting filling every line. Sarah skimmed ahead, catching fragments:

"...found the entrance behind the old furnace..."
"...stairs going down, much deeper than I expected..."
"...shelves and shelves of documents, none of them in the catalog system..."
"...Mrs. Graves found me. She knows what I've discovered..."

And finally, the last entry, dated April 2, 1943:

They're going to seal it. Mrs. Graves told me everything—made me promise to keep the secret. She said it was for the good of the city, that some histories are too dangerous to preserve.

I don't know if I believe her. But I know I'm afraid.

If anything happens to me, if I disappear or die under mysterious circumstances, someone needs to know the truth. The sub-basement exists. The documents are real. And the people who want them hidden are powerful enough to make anyone who knows about them disappear.

I'm leaving this journal in a place where it might be found someday, by someone brave enough or foolish enough to look.

God help them.

That was the final entry. The rest of the pages were blank.

Sarah sat back in her chair, her mind racing. Margaret Ashford's last entry was dated April 2, 1943. Had something happened to her? Had she disappeared, as she feared?

Sarah reached for her laptop again and began to search.

It took less than five minutes to find the obituary.

Margaret Louise Ashford, age 24, died April 5, 1943, in a tragic accident. She apparently fell down a flight of stairs at the City Archive where she worked as a clerk. She is survived by her parents...

An accident. How convenient.

Sarah looked at the journal lying on her table. Margaret had been twenty-four years old. Just a clerk, as they'd called her. Too insignificant to notice.

Until she'd noticed too much.

The archive was scheduled for demolition in three weeks. Whatever was hidden in that sub-basement—if it really existed—would be destroyed along with the building.

Sarah had devoted her life to preserving history, even the difficult parts. Especially the difficult parts.

She closed her laptop and looked at the clock: 2:47 AM.

In a few hours, she would go to the archive. And this time, she would be looking for more than just boxes to pack.

She would be looking for a woman who died eighty years ago, trying to protect a secret that someone had literally killed to keep hidden.

Sarah Mitchell had always considered herself a rational woman.

But as she stared at Margaret Ashford's journal in the early morning light, she knew with absolute certainty that rationality wasn't going to be enough for what came next.`,
      },
    ],
  };

  const progress = Math.round(((currentChapter + 1) / manuscript.totalChapters) * 100);
  const currentChapterData = manuscript.chapters[currentChapter];

  const handleAddNote = () => {
    if (!noteText.trim()) return;

    const newNote = {
      chapter: currentChapter,
      position: 0, // In a real app, would capture scroll position
      text: noteText,
      date: new Date().toISOString().split('T')[0],
    };

    setNotes([...notes, newNote]);
    setNoteText('');
    window.alert('Note added!');
  };

  const handleToggleBookmark = (chapterNum: number) => {
    if (bookmarks.includes(chapterNum)) {
      setBookmarks(bookmarks.filter(b => b !== chapterNum));
    } else {
      setBookmarks([...bookmarks, chapterNum]);
    }
  };

  const handleUpdateProgress = () => {
    console.log('Progress updated:', progress);
    // In real app, would save to backend
  };

  const chapterNotes = notes.filter(note => note.chapter === currentChapter);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      {/* Reading Controls Bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: theme.colors.backgroundAlt,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '16px 0',
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              margin: '0 0 4px 0',
            }}>
              {manuscript.title}
            </h2>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              margin: 0,
            }}>
              by {manuscript.author}
            </p>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <Badge variant="primary">{progress}% Complete</Badge>

            <Button
              size="small"
              variant="outline"
              onClick={() => setFontSize(Math.max(12, fontSize - 2))}
            >
              A-
            </Button>
            <Button
              size="small"
              variant="outline"
              onClick={() => setFontSize(Math.min(24, fontSize + 2))}
            >
              A+
            </Button>

            <Button
              size="small"
              variant={bookmarks.includes(currentChapter) ? 'primary' : 'outline'}
              onClick={() => handleToggleBookmark(currentChapter)}
            >
              {bookmarks.includes(currentChapter) ? '🔖 Bookmarked' : '🔖 Bookmark'}
            </Button>

            <Button
              size="small"
              variant={showNotes ? 'primary' : 'outline'}
              onClick={() => setShowNotes(!showNotes)}
            >
              📝 Notes ({chapterNotes.length})
            </Button>

            <Button
              size="small"
              onClick={() => window.location.href = '/reader/manuscripts'}
            >
              Exit
            </Button>
          </div>
        </div>
      </div>

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '40px 40px 100px 40px',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: showNotes ? '1fr 300px' : '1fr',
          gap: '32px',
        }}>
          {/* Main Reading Area */}
          <div>
            {/* Chapter Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '32px',
              padding: '16px',
              backgroundColor: theme.colors.backgroundAlt,
              borderRadius: theme.borderRadius.medium,
            }}>
              <Button
                size="small"
                variant="outline"
                onClick={() => setCurrentChapter(Math.max(0, currentChapter - 1))}
                disabled={currentChapter === 0}
              >
                ← Previous
              </Button>

              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  marginBottom: '4px',
                }}>
                  Chapter {currentChapterData.number + 1} of {manuscript.totalChapters}
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                }}>
                  {currentChapterData.title}
                </div>
              </div>

              <Button
                size="small"
                variant="outline"
                onClick={() => {
                  setCurrentChapter(Math.min(manuscript.chapters.length - 1, currentChapter + 1));
                  handleUpdateProgress();
                }}
                disabled={currentChapter >= manuscript.chapters.length - 1}
              >
                Next →
              </Button>
            </div>

            {/* Chapter Content */}
            <Card>
              <div style={{
                fontSize: `${fontSize}px`,
                lineHeight: '1.8',
                color: theme.colors.textPrimary,
                whiteSpace: 'pre-wrap',
                fontFamily: theme.fonts.primary,
              }}>
                {currentChapterData.content}
              </div>
            </Card>

            {/* Bottom Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '32px',
            }}>
              <Button
                variant="outline"
                onClick={() => setCurrentChapter(Math.max(0, currentChapter - 1))}
                disabled={currentChapter === 0}
              >
                ← Previous Chapter
              </Button>

              <Button
                onClick={() => {
                  setCurrentChapter(Math.min(manuscript.chapters.length - 1, currentChapter + 1));
                  handleUpdateProgress();
                }}
                disabled={currentChapter >= manuscript.chapters.length - 1}
              >
                Next Chapter →
              </Button>
            </div>
          </div>

          {/* Notes Panel */}
          {showNotes && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
            }}>
              <Card>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  Chapter Notes
                </h3>

                <Textarea
                  placeholder="Add a note about this chapter..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={4}
                />

                <Button
                  size="small"
                  onClick={handleAddNote}
                  style={{ marginTop: '8px', width: '100%' }}
                >
                  Add Note
                </Button>

                <div style={{
                  marginTop: '24px',
                  paddingTop: '24px',
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '12px',
                    color: theme.colors.textSecondary,
                  }}>
                    Previous Notes ({chapterNotes.length})
                  </h4>

                  {chapterNotes.length === 0 ? (
                    <p style={{
                      fontSize: '14px',
                      color: theme.colors.textTertiary,
                      fontStyle: 'italic',
                    }}>
                      No notes for this chapter
                    </p>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}>
                      {chapterNotes.map((note, index) => (
                        <div
                          key={index}
                          style={{
                            padding: '12px',
                            backgroundColor: theme.colors.backgroundAlt,
                            borderRadius: theme.borderRadius.small,
                          }}
                        >
                          <p style={{
                            fontSize: '13px',
                            lineHeight: '1.5',
                            color: theme.colors.textPrimary,
                            marginBottom: '4px',
                          }}>
                            {note.text}
                          </p>
                          <p style={{
                            fontSize: '11px',
                            color: theme.colors.textTertiary,
                          }}>
                            {note.date}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
